import React, { useState, useEffect, useCallback } from 'react';
import { api, supabase } from '../api/supabase';

const fmt = n => Number(n || 0).toLocaleString('ru-RU');
const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD;

// ── Уведомление ──────────────────────────────────────────────
function Msg({ text }) {
  if (!text) return null;
  const ok = text.startsWith('✓');
  return (
    <div style={{
      background: ok ? 'rgba(52,211,153,.1)' : 'rgba(248,113,113,.1)',
      border: `1px solid ${ok ? 'rgba(52,211,153,.3)' : 'rgba(248,113,113,.3)'}`,
      borderRadius: 4, padding: '10px 16px',
      color: ok ? 'var(--green)' : 'var(--red)',
      fontFamily: 'Rajdhani,sans-serif', marginBottom: 16, fontSize: 14,
    }}>{text}</div>
  );
}

// ── Модалка редактирования ───────────────────────────────────
function Modal({ title, onClose, children }) {
  return (
    <div style={{ position:'fixed',inset:0,zIndex:1000,background:'rgba(0,0,0,.7)',display:'flex',alignItems:'center',justifyContent:'center',padding:20 }}>
      <div style={{ background:'var(--panel)',border:'1px solid var(--border2)',borderRadius:4,width:'100%',maxWidth:480,maxHeight:'90vh',overflow:'auto' }}>
        <div style={{ background:'linear-gradient(90deg,var(--panel2),var(--panel))',borderBottom:'1px solid var(--border)',padding:'12px 16px',display:'flex',justifyContent:'space-between',alignItems:'center' }}>
          <span style={{ fontFamily:'Rajdhani,sans-serif',fontWeight:700,color:'var(--blue3)',letterSpacing:1,fontSize:14,textTransform:'uppercase' }}>{title}</span>
          <button onClick={onClose} style={{ background:'none',border:'none',color:'var(--muted)',cursor:'pointer',fontSize:18,lineHeight:1 }}>✕</button>
        </div>
        <div style={{ padding:16 }}>{children}</div>
      </div>
    </div>
  );
}

export const AdminPage = () => {
  const [auth, setAuth] = useState(false);
  const [pwInput, setPwInput] = useState('');
  const [pwError, setPwError] = useState(false);

  const [adminTab, setAdminTab] = useState('bets');
  const [tourns, setTourns] = useState([]);
  const [selectedT, setSelectedT] = useState('');
  const [data, setData] = useState({ matches: [], teams: [], bets: [] });
  const [msg, setMsg] = useState('');

  // Формы добавления
  const [betForm, setBetForm]       = useState({ match_id:'', team_id:'', nick:'', amount:'', type:'match' });
  const [resultForm, setResultForm] = useState({ match_id:'', winner_id:'', score:'' });
  const [tournForm, setTournForm]   = useState({ name:'', type:'Round Robin',date:'',prize:'',margin:'0.05' });
  const [teamForm, setTeamForm]     = useState({ name:'', players:'' });
  const [matchForm, setMatchForm]   = useState({ team_a_id:'', team_b_id:'', format:'Bo5' });

  // Редактирование
  const [editBet,   setEditBet]   = useState(null);
  const [editTourn, setEditTourn] = useState(null);
  const [editTeam,  setEditTeam]  = useState(null);
  const [editMatch, setEditMatch] = useState(null);

  const showMsg = (t) => { setMsg(t); setTimeout(() => setMsg(''), 4000); };

  const reload = useCallback(async (id = selectedT) => {
    if (!id) return;
    const res = await api.getFullTournamentData(id);
    setData(res);
  }, [selectedT]);

  useEffect(() => { api.getTournaments().then(t => { setTourns(t); }); }, []);
  useEffect(() => { reload(selectedT); }, [reload, selectedT]);

  // ── Авторизация ──────────────────────────────────────────
  if (!auth) return (
    <div style={{ background:'var(--bg)',minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center' }}>
      <div style={{ width:320 }}>
        <div style={{ fontFamily:'Rajdhani,sans-serif',fontSize:26,fontWeight:700,color:'var(--blue3)',letterSpacing:3,textTransform:'uppercase',textAlign:'center',marginBottom:24 }}>
          💎 Terminal
        </div>
        <div className="pane">
          <div className="pane-head">🔐 Доступ</div>
          <div className="pane-body">
            <div className="admin-form">
              <input
                type="password" placeholder="Пароль"
                value={pwInput}
                onChange={e => setPwInput(e.target.value)}
                onKeyDown={e => { if (e.key==='Enter') { if (pwInput===ADMIN_PASSWORD) setAuth(true); else setPwError(true); }}}
                style={{ borderColor: pwError ? 'var(--red)' : '' }}
                autoFocus
              />
              {pwError && <div style={{ color:'var(--red)',fontSize:12,fontFamily:'Rajdhani,sans-serif' }}>Неверный пароль</div>}
              <button onClick={() => { if (pwInput===ADMIN_PASSWORD) setAuth(true); else setPwError(true); }}>ВОЙТИ</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const selMatch = data.matches.find(m => m.id === betForm.match_id);

  // ── CRUD: Ставки ─────────────────────────────────────────
  const saveBet = async (e) => {
    e.preventDefault();
    const { error } = await api.placeBet({ ...betForm, tournament_id: selectedT, amount: Number(betForm.amount) });
    if (!error) { showMsg('✓ Ставка добавлена'); setBetForm({ match_id:'',team_id:'',nick:'',amount:'',type:'match' }); reload(); }
    else showMsg('✗ ' + error.message);
  };
  const deleteBet = async (id) => {
    if (!window.confirm('Удалить ставку?')) return;
    const { error } = await supabase.from('bets').delete().eq('id', id);
    if (!error) { showMsg('✓ Ставка удалена'); reload(); }
    else showMsg('✗ ' + error.message);
  };
  const updateBet = async (e) => {
    e.preventDefault();
    const { error } = await supabase.from('bets').update({
      nick: editBet.nick, amount: Number(editBet.amount),
      team_id: editBet.team_id, match_id: editBet.match_id, type: editBet.type,
    }).eq('id', editBet.id);
    if (!error) { showMsg('✓ Ставка обновлена'); setEditBet(null); reload(); }
    else showMsg('✗ ' + error.message);
  };

  // ── CRUD: Результаты матчей ──────────────────────────────
  const saveResult = async (e) => {
    e.preventDefault();
    const { error } = await api.updateMatchResult(resultForm.match_id, {
      winner_id: resultForm.winner_id, score: resultForm.score, status: 'done',
    });
    if (!error) { showMsg('✓ Результат записан'); setResultForm({ match_id:'',winner_id:'',score:'' }); reload(); }
    else showMsg('✗ ' + error.message);
  };
  const resetMatch = async (id) => {
    if (!window.confirm('Сбросить результат матча?')) return;
    const { error } = await supabase.from('matches').update({ status:'pending', winner_id:null, score:null }).eq('id', id);
    if (!error) { showMsg('✓ Результат сброшен'); reload(); }
    else showMsg('✗ ' + error.message);
  };
  const updateMatch = async (e) => {
    e.preventDefault();
    const { error } = await supabase.from('matches').update({
      team_a_id: editMatch.team_a_id, team_b_id: editMatch.team_b_id,
      format: editMatch.format, status: editMatch.status,
      winner_id: editMatch.winner_id || null, score: editMatch.score || null,
    }).eq('id', editMatch.id);
    if (!error) { showMsg('✓ Матч обновлён'); setEditMatch(null); reload(); }
    else showMsg('✗ ' + error.message);
  };
  const deleteMatch = async (id) => {
    if (!window.confirm('Удалить матч? Ставки на него останутся.')) return;
    const { error } = await supabase.from('matches').delete().eq('id', id);
    if (!error) { showMsg('✓ Матч удалён'); reload(); }
    else showMsg('✗ ' + error.message);
  };
  const addMatch = async (e) => {
    e.preventDefault();
    const { error } = await supabase.from('matches').insert([{ ...matchForm, tournament_id: selectedT, status:'pending' }]);
    if (!error) { showMsg('✓ Матч добавлен'); setMatchForm({ team_a_id:'',team_b_id:'',format:'Bo5' }); reload(); }
    else showMsg('✗ ' + error.message);
  };

  // ── CRUD: Турниры ────────────────────────────────────────
  const addTourn = async (e) => {
    e.preventDefault();
    const { data: rows, error } = await supabase.from('tournaments').insert([{
      name: tournForm.name, type: tournForm.type,
      date: tournForm.date || null, prize: tournForm.prize ? Number(tournForm.prize) : null,
      margin: Number(tournForm.margin), is_public: true,
    }]).select();
    if (!error) {
      showMsg('✓ Турнир создан');
      setTournForm({ name:'',type:'Round Robin',date:'',prize:'',margin:'0.05' });
      const updated = await api.getTournaments();
      setTourns(updated);
      if (rows?.[0]) setSelectedT(rows[0].id);
    } else showMsg('✗ ' + error.message);
  };
  const updateTourn = async (e) => {
    e.preventDefault();
    const { error } = await supabase.from('tournaments').update({
      name: editTourn.name, type: editTourn.type, date: editTourn.date,
      prize: editTourn.prize ? Number(editTourn.prize) : null,
      champion: editTourn.champion || null,
      show_match_bets: !!editTourn.show_match_bets,
    }).eq('id', editTourn.id);
    if (!error) {
      showMsg('✓ Турнир обновлён'); setEditTourn(null);
      const updated = await api.getTournaments(); setTourns(updated);
    } else showMsg('✗ ' + error.message);
  };
  const deleteTourn = async (id) => {
    if (!window.confirm('Удалить турнир? Все команды, матчи и ставки удалятся!')) return;
    await supabase.from('bets').delete().eq('tournament_id', id);
    await supabase.from('matches').delete().eq('tournament_id', id);
    await supabase.from('teams').delete().eq('tournament_id', id);
    const { error } = await supabase.from('tournaments').delete().eq('id', id);
    if (!error) {
      showMsg('✓ Турнир удалён');
      const updated = await api.getTournaments(); setTourns(updated);
      setSelectedT(''); setData({ matches:[], teams:[], bets:[] });
    } else showMsg('✗ ' + error.message);
  };

  // ── CRUD: Команды ────────────────────────────────────────
  const addTeam = async (e) => {
    e.preventDefault();
    const { error } = await supabase.from('teams').insert([{
      name: teamForm.name, players: teamForm.players, tournament_id: selectedT,
    }]);
    if (!error) { showMsg('✓ Команда добавлена'); setTeamForm({ name:'',players:'' }); reload(); }
    else showMsg('✗ ' + error.message);
  };
  const updateTeam = async (e) => {
    e.preventDefault();
    const { error } = await supabase.from('teams').update({ name: editTeam.name, players: editTeam.players }).eq('id', editTeam.id);
    if (!error) { showMsg('✓ Команда обновлена'); setEditTeam(null); reload(); }
    else showMsg('✗ ' + error.message);
  };
  const deleteTeam = async (id) => {
    if (!window.confirm('Удалить команду?')) return;
    const { error } = await supabase.from('teams').delete().eq('id', id);
    if (!error) { showMsg('✓ Команда удалена'); reload(); }
    else showMsg('✗ ' + error.message);
  };

  const tabs = [
    ['bets','💰 Ставки'],['results','🗡 Матчи'],['teams','⚔ Команды'],['tournaments','🏆 Турниры'],
  ];

  const inp = { style:{ width:'100%', marginBottom:8, padding:'8px 12px', background:'var(--panel2)', border:'1px solid var(--border)', color:'var(--text)', fontFamily:'Noto Serif,serif', fontSize:14, borderRadius:3, outline:'none', boxSizing:'border-box' }};
  const sel = { style:{ width:'100%', marginBottom:8, padding:'8px 12px', background:'var(--panel2)', border:'1px solid var(--border)', color:'var(--text)', fontFamily:'Noto Serif,serif', fontSize:14, borderRadius:3, outline:'none', cursor:'pointer', boxSizing:'border-box' }};
  const btn = (color='blue') => ({ style:{ padding:'8px 16px', background: color==='red'?'rgba(248,113,113,.15)':'rgba(59,130,246,.15)', border:`1px solid ${color==='red'?'rgba(248,113,113,.4)':'rgba(59,130,246,.4)'}`, color: color==='red'?'var(--red)':'var(--blue2)', fontFamily:'Rajdhani,sans-serif', fontSize:12, fontWeight:700, letterSpacing:1, cursor:'pointer', borderRadius:3, textTransform:'uppercase', marginRight:6 }});
  const btnSm = (color='blue') => ({ style:{ padding:'4px 10px', background: color==='red'?'rgba(248,113,113,.1)':'rgba(59,130,246,.1)', border:`1px solid ${color==='red'?'rgba(248,113,113,.3)':'rgba(59,130,246,.3)'}`, color: color==='red'?'var(--red)':'var(--blue2)', fontFamily:'Rajdhani,sans-serif', fontSize:11, fontWeight:700, letterSpacing:1, cursor:'pointer', borderRadius:2, textTransform:'uppercase', marginRight:4, whiteSpace:'nowrap' }});

  return (
    <div style={{ background:'var(--bg)',minHeight:'100vh',padding:'20px' }}>
      <div style={{ maxWidth:900,margin:'0 auto',position:'relative',zIndex:1 }}>

        {/* Шапка */}
        <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20 }}>
          <div style={{ fontFamily:'Rajdhani,sans-serif',fontSize:22,fontWeight:700,color:'var(--blue3)',letterSpacing:3,textTransform:'uppercase' }}>
            💎 Terminal WhiteWolf
          </div>
          <div style={{ display:'flex',alignItems:'center',gap:12 }}>
            <select {...sel} style={{...sel.style,width:'auto',marginBottom:0}} value={selectedT} onChange={e => setSelectedT(e.target.value)}>
              <option value="">— Турнир —</option>
              {tourns.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <button {...btn('red')} onClick={() => setAuth(false)}>ВЫЙТИ</button>
          </div>
        </div>

        <Msg text={msg} />

        {/* Вкладки */}
        <div style={{ display:'flex',gap:0,marginBottom:20,borderBottom:'1px solid var(--border)' }}>
          {tabs.map(([id,label]) => (
            <button key={id} onClick={() => setAdminTab(id)} style={{
              padding:'10px 16px', background:'none', border:'none', borderBottom: adminTab===id?'2px solid var(--blue)':'2px solid transparent',
              color: adminTab===id?'var(--blue2)':'var(--text2)', fontFamily:'Rajdhani,sans-serif',
              fontSize:13, fontWeight:700, letterSpacing:1, cursor:'pointer', textTransform:'uppercase',
            }}>{label}</button>
          ))}
        </div>

        {/* ══ СТАВКИ ══ */}
        {adminTab==='bets' && (<>
          <div className="pane" style={{marginBottom:20}}>
            <div className="pane-head">➕ Добавить ставку</div>
            <div className="pane-body">
              <form onSubmit={saveBet}>
                <select {...sel} value={betForm.match_id} onChange={e=>setBetForm({...betForm,match_id:e.target.value,team_id:''})} required disabled={!selectedT}>
                  <option value="">Матч...</option>
                  {data.matches.map((m,i)=><option key={m.id} value={m.id}>М-{String(i+1).padStart(2,'0')} · {m.teamA?.name} vs {m.teamB?.name}</option>)}
                </select>
                <select {...sel} value={betForm.team_id} onChange={e=>setBetForm({...betForm,team_id:e.target.value})} required>
                  <option value="">Команда...</option>
                  {(selMatch ? [selMatch.teamA,selMatch.teamB].filter(Boolean) : data.teams).map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                <select {...sel} value={betForm.type} onChange={e=>setBetForm({...betForm,type:e.target.value})}>
                  <option value="match">На матч</option>
                  <option value="champion">На чемпиона</option>
                </select>
                <input {...inp} placeholder="Ник" value={betForm.nick} onChange={e=>setBetForm({...betForm,nick:e.target.value})} required />
                <input {...inp} type="number" placeholder="Сумма (адена)" value={betForm.amount} onChange={e=>setBetForm({...betForm,amount:e.target.value})} required min="1" />
                <button {...btn()} type="submit">ДОБАВИТЬ СТАВКУ</button>
              </form>
            </div>
          </div>

          <div className="pane">
            <div className="pane-head">📋 Все ставки ({data.bets.length})</div>
            <div style={{overflowX:'auto'}}>
              <table className="data-table">
                <thead><tr><th>Ник</th><th>Матч</th><th>Команда</th><th>Тип</th><th>Сумма</th><th>Действия</th></tr></thead>
                <tbody>
                  {data.bets.map((b,i) => {
                    const m = data.matches.find(x=>x.id===b.match_id);
                    const team = data.teams.find(t=>t.id===b.team_id);
                    return (
                      <tr key={b.id||i}>
                        <td style={{fontFamily:'Rajdhani,sans-serif',color:'var(--blue2)',fontWeight:600}}>{b.nick}</td>
                        <td style={{fontFamily:'JetBrains Mono,monospace',fontSize:11,color:'var(--muted)'}}>{m?`${m.teamA?.name} vs ${m.teamB?.name}`:'ЧЕМПИОН'}</td>
                        <td>{team?.name||'—'}</td>
                        <td><span className={`badge ${b.type==='champion'?'badge-active':'badge-type'}`}>{b.type}</span></td>
                        <td style={{fontFamily:'JetBrains Mono,monospace',fontSize:13,color:'var(--text2)'}}>{fmt(b.amount)} ₳</td>
                        <td style={{whiteSpace:'nowrap'}}>
                          <button {...btnSm()} onClick={()=>setEditBet({...b})}>✎ Изменить</button>
                          <button {...btnSm('red')} onClick={()=>deleteBet(b.id)}>✕ Удалить</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>)}

        {/* ══ МАТЧИ ══ */}
        {adminTab==='results' && (<>
          <div className="pane" style={{marginBottom:20}}>
            <div className="pane-head">➕ Добавить матч</div>
            <div className="pane-body">
              <form onSubmit={addMatch}>
                <select {...sel} value={matchForm.team_a_id} onChange={e=>setMatchForm({...matchForm,team_a_id:e.target.value})} required disabled={!selectedT}>
                  <option value="">Команда A...</option>
                  {data.teams.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                <select {...sel} value={matchForm.team_b_id} onChange={e=>setMatchForm({...matchForm,team_b_id:e.target.value})} required>
                  <option value="">Команда B...</option>
                  {data.teams.filter(t=>t.id!==matchForm.team_a_id).map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                <select {...sel} value={matchForm.format} onChange={e=>setMatchForm({...matchForm,format:e.target.value})}>
                  <option value="Bo5">Bo5</option><option value="Bo3">Bo3</option><option value="Bo1">Bo1</option>
                </select>
                <button {...btn()} type="submit">ДОБАВИТЬ МАТЧ</button>
              </form>
            </div>
          </div>

          <div className="pane" style={{marginBottom:20}}>
            <div className="pane-head">🗡 Записать результат</div>
            <div className="pane-body">
              <form onSubmit={saveResult}>
                <select {...sel} value={resultForm.match_id} onChange={e=>setResultForm({...resultForm,match_id:e.target.value,winner_id:''})} required disabled={!selectedT}>
                  <option value="">Матч (незавершённые)...</option>
                  {data.matches.filter(m=>m.status!=='done').map((m,i)=><option key={m.id} value={m.id}>М-{String(i+1).padStart(2,'0')} · {m.teamA?.name} vs {m.teamB?.name}</option>)}
                </select>
                {(() => { const m=data.matches.find(x=>x.id===resultForm.match_id); return m?(
                  <select {...sel} value={resultForm.winner_id} onChange={e=>setResultForm({...resultForm,winner_id:e.target.value})} required>
                    <option value="">Победитель...</option>
                    {[m.teamA,m.teamB].filter(Boolean).map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                ):null; })()}
                <input {...inp} placeholder="Счёт (3:1)" value={resultForm.score} onChange={e=>setResultForm({...resultForm,score:e.target.value})} />
                <button {...btn()} type="submit">ЗАПИСАТЬ РЕЗУЛЬТАТ</button>
              </form>
            </div>
          </div>

          <div className="pane">
            <div className="pane-head">📋 Все матчи ({data.matches.length})</div>
            <div style={{overflowX:'auto'}}>
              <table className="data-table">
                <thead><tr><th>№</th><th>A</th><th>B</th><th>Формат</th><th>Статус</th><th>Победитель</th><th>Счёт</th><th>Действия</th></tr></thead>
                <tbody>
                  {data.matches.map((m,i)=>{
                    const winner=data.teams.find(t=>t.id===m.winner_id);
                    return (
                      <tr key={m.id}>
                        <td className="mid">М-{String(i+1).padStart(2,'0')}</td>
                        <td className="mteam">{m.teamA?.name}</td>
                        <td className="mteam">{m.teamB?.name}</td>
                        <td className="mid">{m.format}</td>
                        <td>{m.status==='done'?<span className="txt-win">Done</span>:m.status==='live'?<span className="dot dot-live"/>:<span className="txt-pend">pending</span>}</td>
                        <td>{winner?<span className="winner-tag">{winner.name}</span>:'—'}</td>
                        <td className="mid">{m.score||'—'}</td>
                        <td style={{whiteSpace:'nowrap'}}>
                          <button {...btnSm()} onClick={()=>setEditMatch({...m,team_a_id:m.team_a_id||m.teamA?.id,team_b_id:m.team_b_id||m.teamB?.id,winner_id:m.winner_id||'',score:m.score||''})}>✎</button>
                          {m.status==='done'&&<button {...btnSm()} onClick={()=>resetMatch(m.id)}>↺ Сброс</button>}
                          <button {...btnSm('red')} onClick={()=>deleteMatch(m.id)}>✕</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>)}

        {/* ══ КОМАНДЫ ══ */}
        {adminTab==='teams' && (<>
          <div className="pane" style={{marginBottom:20}}>
            <div className="pane-head">➕ Добавить команду</div>
            <div className="pane-body">
              <form onSubmit={addTeam}>
                <input {...inp} placeholder="Название команды" value={teamForm.name} onChange={e=>setTeamForm({...teamForm,name:e.target.value})} required disabled={!selectedT} />
                <input {...inp} placeholder="Игроки (через запятую)" value={teamForm.players} onChange={e=>setTeamForm({...teamForm,players:e.target.value})} />
                <button {...btn()} type="submit">ДОБАВИТЬ КОМАНДУ</button>
              </form>
            </div>
          </div>

          <div className="pane">
            <div className="pane-head">⚔ Команды ({data.teams.length})</div>
            <div style={{overflowX:'auto'}}>
              <table className="data-table">
                <thead><tr><th>Название</th><th>Игроки</th><th>Действия</th></tr></thead>
                <tbody>
                  {data.teams.map(t=>(
                    <tr key={t.id}>
                      <td className="mteam">{t.name}</td>
                      <td style={{fontSize:12,color:'var(--text2)',fontFamily:'JetBrains Mono,monospace'}}>{t.players||'—'}</td>
                      <td style={{whiteSpace:'nowrap'}}>
                        <button {...btnSm()} onClick={()=>setEditTeam({...t})}>✎ Изменить</button>
                        <button {...btnSm('red')} onClick={()=>deleteTeam(t.id)}>✕ Удалить</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>)}

        {/* ══ ТУРНИРЫ ══ */}
        {adminTab==='tournaments' && (<>
          <div className="pane" style={{marginBottom:16}}>
            <div className="pane-head">👁 Видимость вкладки «Ставки»</div>
            <div className="pane-body" style={{display:'flex',alignItems:'center',gap:16}}>
              <span style={{fontFamily:'Rajdhani,sans-serif',fontSize:13,color:'var(--text2)'}}>
                Вкладка «📋 Ставки» сейчас:
                <strong style={{marginLeft:8,color:localStorage.getItem('betsVisible')==='false'?'var(--red)':'var(--green)'}}>
                  {localStorage.getItem('betsVisible')==='false'?'СКРЫТА':'ВИДНА'}
                </strong>
              </span>
              <button {...btn(localStorage.getItem('betsVisible')==='false'?'blue':'red')} onClick={()=>{
                const next = localStorage.getItem('betsVisible')==='false' ? 'true' : 'false';
                localStorage.setItem('betsVisible', next);
                showMsg(next==='true'?'✓ Вкладка Ставки теперь видна':'✓ Вкладка Ставки скрыта');
                window.location.reload();
              }}>
                {localStorage.getItem('betsVisible')==='false'?'ПОКАЗАТЬ':'СКРЫТЬ'}
              </button>
            </div>
          </div>
          <div className="pane" style={{marginBottom:20}}>
            <div className="pane-head">➕ Создать турнир</div>
            <div className="pane-body">
              <form onSubmit={addTourn}>
                <input {...inp} placeholder="Название турнира" value={tournForm.name} onChange={e=>setTournForm({...tournForm,name:e.target.value})} required />
                <input {...inp} placeholder="Тип (Round Robin, Плей-офф...)" value={tournForm.type} onChange={e=>setTournForm({...tournForm,type:e.target.value})} />
                <input {...inp} placeholder="Дата (например: Март 2025)" value={tournForm.date} onChange={e=>setTournForm({...tournForm,date:e.target.value})} />
                <input {...inp} type="number" placeholder="Призовой фонд (адена, необязательно)" value={tournForm.prize} onChange={e=>setTournForm({...tournForm,prize:e.target.value})} />
                <input {...inp} type="number" step="0.01" placeholder="Маржа (0.05 = 5%)" value={tournForm.margin} onChange={e=>setTournForm({...tournForm,margin:e.target.value})} />
                <button {...btn()} type="submit">СОЗДАТЬ ТУРНИР</button>
              </form>
            </div>
          </div>

          <div className="pane">
            <div className="pane-head">🏆 Все турниры</div>
            <div style={{overflowX:'auto'}}>
              <table className="data-table">
                <thead><tr><th>Название</th><th>Тип</th><th>Дата</th><th>Приз</th><th>Чемпион</th><th>На матчи</th><th>Действия</th></tr></thead>
                <tbody>
                  {tourns.map(t=>(
                    <tr key={t.id}>
                      <td className="mteam">{t.name}</td>
                      <td className="mid">{t.type||'—'}</td>
                      <td className="mid">{t.date||'—'}</td>
                      <td className="adena">{t.prize?fmt(t.prize)+' ₳':'—'}</td>
                      <td>{t.champion?<span className="winner-tag">{t.champion}</span>:'—'}</td>
                      <td>{t.show_match_bets?<span className="txt-win">✓ Вкл</span>:<span className="txt-pend">Выкл</span>}</td>
                      <td style={{whiteSpace:'nowrap'}}>
                        <button {...btnSm()} onClick={()=>setEditTourn({...t,prize:t.prize||'',champion:t.champion||'',show_match_bets:!!t.show_match_bets})}>✎ Изменить</button>
                        <button {...btnSm('red')} onClick={()=>deleteTourn(t.id)}>✕ Удалить</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>)}

      </div>

      {/* ══ МОДАЛКИ РЕДАКТИРОВАНИЯ ══ */}

      {editBet && (
        <Modal title="✎ Редактировать ставку" onClose={()=>setEditBet(null)}>
          <form onSubmit={updateBet}>
            <input {...inp} placeholder="Ник" value={editBet.nick} onChange={e=>setEditBet({...editBet,nick:e.target.value})} required />
            <select {...sel} value={editBet.match_id||''} onChange={e=>setEditBet({...editBet,match_id:e.target.value})}>
              <option value="">ЧЕМПИОН</option>
              {data.matches.map((m,i)=><option key={m.id} value={m.id}>М-{String(i+1).padStart(2,'0')} · {m.teamA?.name} vs {m.teamB?.name}</option>)}
            </select>
            <select {...sel} value={editBet.team_id} onChange={e=>setEditBet({...editBet,team_id:e.target.value})}>
              {data.teams.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <select {...sel} value={editBet.type} onChange={e=>setEditBet({...editBet,type:e.target.value})}>
              <option value="match">match</option><option value="champion">champion</option>
            </select>
            <input {...inp} type="number" placeholder="Сумма" value={editBet.amount} onChange={e=>setEditBet({...editBet,amount:e.target.value})} required />
            <div style={{display:'flex',gap:8,marginTop:4}}>
              <button {...btn()} type="submit">СОХРАНИТЬ</button>
              <button {...btn('red')} type="button" onClick={()=>setEditBet(null)}>ОТМЕНА</button>
            </div>
          </form>
        </Modal>
      )}

      {editMatch && (
        <Modal title="✎ Редактировать матч" onClose={()=>setEditMatch(null)}>
          <form onSubmit={updateMatch}>
            <select {...sel} value={editMatch.team_a_id} onChange={e=>setEditMatch({...editMatch,team_a_id:e.target.value})}>
              {data.teams.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <select {...sel} value={editMatch.team_b_id} onChange={e=>setEditMatch({...editMatch,team_b_id:e.target.value})}>
              {data.teams.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <select {...sel} value={editMatch.format} onChange={e=>setEditMatch({...editMatch,format:e.target.value})}>
              <option value="Bo5">Bo5</option><option value="Bo3">Bo3</option><option value="Bo1">Bo1</option>
            </select>
            <select {...sel} value={editMatch.status} onChange={e=>setEditMatch({...editMatch,status:e.target.value})}>
              <option value="pending">pending</option><option value="live">live</option><option value="done">done</option>
            </select>
            <select {...sel} value={editMatch.winner_id||''} onChange={e=>setEditMatch({...editMatch,winner_id:e.target.value})}>
              <option value="">Победитель (если done)...</option>
              {data.teams.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <input {...inp} placeholder="Счёт (3:1)" value={editMatch.score||''} onChange={e=>setEditMatch({...editMatch,score:e.target.value})} />
            <div style={{display:'flex',gap:8,marginTop:4}}>
              <button {...btn()} type="submit">СОХРАНИТЬ</button>
              <button {...btn('red')} type="button" onClick={()=>setEditMatch(null)}>ОТМЕНА</button>
            </div>
          </form>
        </Modal>
      )}

      {editTeam && (
        <Modal title="✎ Редактировать команду" onClose={()=>setEditTeam(null)}>
          <form onSubmit={updateTeam}>
            <input {...inp} placeholder="Название" value={editTeam.name} onChange={e=>setEditTeam({...editTeam,name:e.target.value})} required />
            <input {...inp} placeholder="Игроки (через запятую)" value={editTeam.players||''} onChange={e=>setEditTeam({...editTeam,players:e.target.value})} />
            <div style={{display:'flex',gap:8,marginTop:4}}>
              <button {...btn()} type="submit">СОХРАНИТЬ</button>
              <button {...btn('red')} type="button" onClick={()=>setEditTeam(null)}>ОТМЕНА</button>
            </div>
          </form>
        </Modal>
      )}

      {editTourn && (
        <Modal title="✎ Редактировать турнир" onClose={()=>setEditTourn(null)}>
          <form onSubmit={updateTourn}>
            <input {...inp} placeholder="Название" value={editTourn.name} onChange={e=>setEditTourn({...editTourn,name:e.target.value})} required />
            <input {...inp} placeholder="Тип" value={editTourn.type||''} onChange={e=>setEditTourn({...editTourn,type:e.target.value})} />
            <input {...inp} placeholder="Дата" value={editTourn.date||''} onChange={e=>setEditTourn({...editTourn,date:e.target.value})} />
            <input {...inp} type="number" placeholder="Призовой фонд" value={editTourn.prize||''} onChange={e=>setEditTourn({...editTourn,prize:e.target.value})} />
            <input {...inp} placeholder="Чемпион (название команды)" value={editTourn.champion||''} onChange={e=>setEditTourn({...editTourn,champion:e.target.value})} />
            <label style={{display:'flex',alignItems:'center',gap:10,cursor:'pointer',fontFamily:'Rajdhani,sans-serif',fontSize:13,color:'var(--text2)',padding:'6px 0'}}>
              <input
                type="checkbox"
                checked={!!editTourn.show_match_bets}
                onChange={e=>setEditTourn({...editTourn,show_match_bets:e.target.checked})}
                style={{width:16,height:16,cursor:'pointer',accentColor:'var(--blue)'}}
              />
              Показывать «На матчи» во вкладке Коэффициенты
            </label>
            <div style={{display:'flex',gap:8,marginTop:4}}>
              <button {...btn()} type="submit">СОХРАНИТЬ</button>
              <button {...btn('red')} type="button" onClick={()=>setEditTourn(null)}>ОТМЕНА</button>
            </div>
          </form>
        </Modal>
      )}

    </div>
  );
};
